/**
 * The gateway seam — what happened on a `/api/aki` call, as opposed to how it was made.
 *
 * Split out of `./client.ts` during WP-575 (TEN-2917) at the 500-LOC ratchet's insistence
 * (STD-2 / DEC-1504), and the ratchet was reading the design correctly: client.ts's job is
 * to MAKE the call — resolve the deployment, attach auth, parse the envelope, cache reads.
 * Recording what the call cost, classifying how it failed, and deciding whether the caller
 * may safely retry are a different job with a different reason to change. Everything here
 * is downstream-of-the-response; nothing here knows how to send one.
 *
 * Deliberately free of any dependency on client.ts, so there is no import cycle: the two
 * pieces of per-call context this module cannot derive (the resolved workspace and the
 * active tool context) are PASSED IN by the caller rather than reached for.
 *
 * `./client.ts` re-exports this module's public surface, so existing importers (and the
 * test mocks that enumerate client.js's exports) keep working unchanged.
 *
 * What a tenant's calls ADD UP TO — the per-workspace audit buffer and the rolling counters —
 * moved to ./lib/gatewaySeamStore.ts in the PR #533 review round (STD-2 / DEC-1504 again:
 * classifying one failure and remembering a tenant's history are different jobs). Its surface
 * is re-exported below so this module stays the single import point for the seam.
 */
import { trackToolCall } from "./analytics.js";
import { ROUTE_TYPE_BY_NAME } from "./generated/routeLatencyBudget.generated.js";
import { appendAuditEntry, recordSeamCounters, type AuditEntry } from "./lib/gatewaySeamStore.js";

export {
  formatGatewaySeamSummary,
  getAuditLog,
  getGatewaySeamCounters,
  getMergedGatewaySeamCounters,
  __resetGatewaySeamCountersForTest,
  type AuditEntry,
  type GatewaySeamCounters,
} from "./lib/gatewaySeamStore.js";

/** Convex `/api/aki` error body — 4xx/5xx include `error`; structured codes include `code`. */
export class KernelCallError extends Error {
  readonly status: number;
  readonly code?: string;
  /** WP-316 S1a: Structured commit validation — required field keys missing from entry.data. */
  readonly missingRequiredFields?: string[];
  /** WP-316 S1a: Structured commit validation — field-level data errors. */
  readonly fieldErrors?: string[];
  /**
   * WP-465 slice ⑤: structured diagnostics carried by an `ok:false` kernel envelope
   * (e.g. `coherencyRefusals`, `blockers`). The gateway forwards these verbatim at
   * HTTP 200; without preserving them here a refused/blocked envelope would collapse
   * into a bare code+message and the caller could not surface the per-offender routes.
   */
  readonly diagnostics?: Record<string, unknown>;
  constructor(
    message: string,
    status: number,
    code?: string,
    missingRequiredFields?: string[],
    fieldErrors?: string[],
    diagnostics?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "KernelCallError";
    this.status = status;
    this.code = code;
    this.missingRequiredFields = missingRequiredFields;
    this.fieldErrors = fieldErrors;
    this.diagnostics = diagnostics;
  }
}

/**
 * A gateway call that hit its declared latency budget (WP-575, TEN-2917).
 *
 * WHY THIS IS ITS OWN TYPE and not folded into the generic network error client.ts used to
 * throw for every `fetch` rejection: a budget abort and a connection refusal are opposite
 * facts about the server, and the caller has to be able to tell them apart. A refusal proves
 * nothing ran. A budget abort proves only that WE stopped waiting — the server may well have
 * completed the write. Collapsing both into "network error" is what let MCP report a hard
 * failure on `chain.createEntry` calls that had SUCCEEDED, and left callers with no way to
 * say so. `mayHaveLanded` carries that distinction structurally so a caller can surface a
 * partial-success signal instead of a bare failure (see tools/smart-capture.ts).
 *
 * The `name` below is a LOAD-BEARING contract, pinned by a test: smart-capture discriminates
 * on it rather than `instanceof`, because class identity survives neither a mocked module
 * nor module duplication by a bundler.
 */
export class GatewayTimeoutError extends Error {
  /** Gateway route name that was aborted. */
  readonly fn: string;
  /** The declared budget, in ms, that this route was given (see routeLatencyBudget). */
  readonly budgetMs: number;
  /** Wall-clock ms actually spent before the abort. */
  readonly elapsedMs: number;
  /**
   * True when the aborted call could have landed a write server-side despite this client
   * giving up. Derived by `mayHaveLandedOnTimeout` from the route contract's own function
   * type (`mutation`/`action` mutate; `query` does not) AND the call's own arguments (a
   * `preview: true` dry run writes nothing) — never guessed from the name.
   */
  readonly mayHaveLanded: boolean;
  constructor(fn: string, budgetMs: number, elapsedMs: number, mayHaveLanded: boolean) {
    super(
      `MCP call "${fn}" exceeded its ${budgetMs}ms latency budget (waited ${elapsedMs}ms).` +
        (mayHaveLanded
          ? " This route writes, so the server may have completed it — verify before retrying."
          : ""),
    );
    this.name = "GatewayTimeoutError";
    this.fn = fn;
    this.budgetMs = budgetMs;
    this.elapsedMs = elapsedMs;
    this.mayHaveLanded = mayHaveLanded;
  }
}

/**
 * Does this route mutate state? (WP-575)
 *
 * Read off the route contract's own Convex function type — `mutation` and `action` can
 * write, `query` cannot — never inferred from the route's NAME. Name-based guessing is how
 * client.ts's read-cache `isWrite` has to work (it predates the typed contract and covers
 * cache invalidation, where over-invalidating is merely wasteful), but the stakes here are
 * different: this decides whether the agent is told a timed-out write may have landed.
 * Telling it "nothing landed" when something did is the worse error, so an UNKNOWN route
 * fails safe to `true` — same direction as the budget's own unknown-name fallback.
 */
export function routeMayMutate(fn: string): boolean {
  const type = ROUTE_TYPE_BY_NAME[fn];
  return type === undefined || type !== "query";
}

/**
 * Was this call a DRY RUN? (PR #533 review, Codex P2)
 *
 * `preview: true` is a gateway-wide contract meaning "run every validation, write nothing" —
 * not a per-route convenience. Every registered route that accepts the argument returns before
 * its first write, verified one by one: `chain.createEntry`
 * (convex/agentKnowledge/entries.ts:498, returning at :583-599 with the first write at :601 —
 * and its action wrapper skips the contradiction detector entirely at :915),
 * `chain.commitEntry` (:2537, before createPublishedVersion/createProposalForEntry/
 * recordSessionActivity), `chain.createEntryRelation`
 * (convex/agentKnowledge/relations.ts:152 and :186, before any insert or scheduler), and
 * `quality.evaluateHeuristicAndSchedule` (convex/intelligence/qualityCoaching.ts:1101,
 * documented "NEVER persist, schedule, or stamp", before the insert at :1110).
 */
export function isDryRunCall(args: unknown): boolean {
  return (args as { preview?: unknown } | null | undefined)?.preview === true;
}

/**
 * Could a timed-out call have LANDED A WRITE? (PR #533 review, Codex P2)
 *
 * The route's type answers "can this route write at all"; the call's own arguments answer
 * "was this particular call asking it to". Both are needed, and deciding it here — once,
 * where both are in scope — is what keeps the four surfaces that report a timeout consistent:
 * `GatewayTimeoutError`'s message, lib/captureTimeoutOutcome.ts, lib/batchTimeoutCohort.ts,
 * and the batch-preview markdown in lib/batchCaptureOutput.ts. Deriving it from the type
 * alone told a dry run its write "may have completed" and warned against retrying — beside
 * that same response's own "no DB writes" line.
 *
 * The fail-safe direction is unchanged for everything else: an unknown route still mutates
 * as far as we know (`routeMayMutate`), because claiming "nothing landed" when something did
 * is the worse error. A dry run is the one case where "nothing landed" is a CONTRACT, not a
 * guess — see `isDryRunCall` for the route-by-route verification.
 */
export function mayHaveLandedOnTimeout(fn: string, args: unknown): boolean {
  return routeMayMutate(fn) && !isDryRunCall(args);
}

/**
 * Classify a failed gateway call and throw. Shared by BOTH phases a call can fail in — the
 * headers phase (`fetch` rejects) and the body phase (`res.json()` rejects while the abort
 * signal is still live, PR #533 review Codex P2) — because the phase changes what happened,
 * not what it MEANS.
 *
 * WP-575: a budget abort and a connection failure are opposite facts, and only the former
 * leaves the server's own outcome unknown. `AbortSignal.timeout` rejects with a DOMException
 * named "TimeoutError"; a caller-driven abort surfaces as "AbortError". Treat both as "we
 * stopped waiting", never as "the server failed."
 *
 * `record` is injected rather than called directly so this module keeps owing nothing to
 * client.ts (see the file header) — the caller supplies the workspace/tool context.
 *
 * `args` is taken rather than a pre-computed `mayHaveLanded` so the derivation stays inside
 * this module (`mayHaveLandedOnTimeout`): two call sites computing it themselves is how the
 * two connectors drifted apart in the first place.
 */
export function throwClassifiedGatewayFailure(
  err: any,
  fn: string,
  args: unknown,
  budgetMs: number,
  elapsedMs: number,
  phase: "network" | "response body",
  record: (auditMsg: string, timedOut: boolean) => void,
): never {
  if (err?.name === "TimeoutError" || err?.name === "AbortError") {
    const timeoutErr = new GatewayTimeoutError(fn, budgetMs, elapsedMs, mayHaveLandedOnTimeout(fn, args));
    record(timeoutErr.message, true);
    throw timeoutErr;
  }
  const detail = err?.message ?? String(err);
  record(phase === "network" ? detail : `${phase}: ${detail}`, false);
  throw new Error(`MCP call "${fn}" ${phase} error: ${detail}`);
}


/**
 * What an unreadable response BODY means — and it depends on the status.
 *
 * `fetch` resolves at headers while the budget signal is still live, so a stalled body aborts
 * after the call already succeeded at the transport level. Two different situations land here
 * and they deserve opposite answers (PR #533 review round 6):
 *
 *   - **Status was OK** — a 2xx whose body never arrived. The server's outcome is genuinely
 *     unknown, which is the timeout this work package exists to name. Classify and throw.
 *   - **Status was NOT OK** — the server reached a verdict and reported it; only its error body
 *     stalled. Calling that a budget timeout trades a real status and code (a 429's
 *     `retryAfterSeconds`, a 4xx's validation code) for "the server may have completed it
 *     anyway, verify before retrying" — about a call the server explicitly failed. Yield an
 *     empty body instead and let the caller's own status branch report it, which is what
 *     non-OK responses have always done.
 *
 * Lives here rather than in the caller's catch because this is the taxonomy's question, not
 * the call-maker's: client.ts sends requests, this module decides what a failure MEANT.
 */
export function emptyBodyOrThrowClassified<T>(
  err: any,
  fn: string,
  args: unknown,
  budgetMs: number,
  elapsedMs: number,
  statusOk: boolean,
  record: (auditMsg: string, timedOut: boolean) => void,
): T {
  if (statusOk) throwClassifiedGatewayFailure(err, fn, args, budgetMs, elapsedMs, "response body", record);
  return {} as T;
}

// ─── Recording one call ───────────────────────────────────────────────

function shouldLogAudit(status: "ok" | "error"): boolean {
  return status === "error" || process.env.MCP_DEBUG === "1";
}

/**
 * Record one completed gateway call: buffer entry, cumulative counters, PostHog, stderr.
 *
 * `workspace` and `toolContext` are parameters rather than module-level lookups precisely so
 * this module owes nothing to client.ts — see the file header.
 */
export function recordGatewayCall(params: {
  fn: string;
  status: "ok" | "error";
  durationMs: number;
  workspace: string;
  errorMsg?: string;
  toolContext?: { tool: string; action?: string } | null;
  /** WP-575: the budget this call was judged by, and whether it was what killed it. */
  budgetMs?: number;
  timedOut?: boolean;
}): void {
  const { fn, status, durationMs, workspace, errorMsg, toolContext, budgetMs, timedOut } = params;
  const ts = new Date().toISOString();

  const entry: Omit<AuditEntry, "seq"> = { ts, fn, workspace, status, durationMs };
  if (errorMsg) entry.error = errorMsg;
  if (toolContext) entry.toolContext = toolContext;
  if (budgetMs !== undefined) entry.budgetMs = budgetMs;
  if (timedOut) entry.timedOut = true;
  appendAuditEntry(entry);

  recordSeamCounters(workspace, fn, status, durationMs, timedOut === true);
  trackToolCall(fn, status, durationMs, workspace, errorMsg);

  if (!shouldLogAudit(status)) return;

  const base =
    `[MCP-AUDIT] ${ts} fn=${fn} workspace=${workspace} status=${status} duration=${durationMs}ms` +
    `${budgetMs !== undefined ? ` budget=${budgetMs}ms` : ""}${timedOut ? " timedOut=true" : ""}`;
  process.stderr.write(
    status === "error" && errorMsg ? `${base} error=${JSON.stringify(errorMsg)}\n` : `${base}\n`,
  );
}

